import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  resource,
  signal,
  viewChild
} from "@angular/core";
import { Zoom } from "../shared/interfaces/mockup.interface";
import { ProcessResponse } from "../shared/interfaces/process-response.interface";

type Zone = { x: number; y: number; width: number; height: number };

@Component({
  selector: 'app-canvas',
  standalone: true,
  templateUrl: './canvas.component.html',
  imports: [],
  styleUrls: ['./canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CanvasComponent {
  backImageUrl = input.required<string>();
  frontMediaFile = input<ProcessResponse>();
  zoom = input<number>(1);
  zoomList = input<Zoom[]>([{ name: 'Ajusté', scale: 1 }]);
  private container = viewChild.required<ElementRef<HTMLDivElement>>('container');
  private canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private context = computed(() => {
    const canvas = this.canvas().nativeElement;
    const pixelRatio = this.pixelRatio()
    const width = this.width();
    const backImage = this.backImage.value();
    if (!backImage) {
      return;
    }
    const height = width * backImage.height / backImage.width;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    const ctx = canvas.getContext('2d')!
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.scale(pixelRatio, pixelRatio);
    return ctx;
  });

  private backImage = resource({
    params: () => this.backImageUrl(),
    loader: ({params: url}) => {
      return new Promise<HTMLImageElement>(resolve => {
        const img = new Image()
        img.src = url;
        img.onload = () => {
          resolve(img);
        }
      })
    }
  });

  private transparentZones = computed<[number, Zone][]>(() => {
    const ctx = this.context();
    const zoomList = this.zoomList();
    const backImage = this.backImage.value();
    const pixelRatio = this.pixelRatio()
    const width = this.width();
    if (!ctx || !zoomList || !backImage || !pixelRatio || !width) {
      return [[1, { x: 0, y: 0, width: 0, height: 0 } as Zone]]
    }
    const zones: [number, Zone][] = []
    for (const zoom of zoomList) {
      const scale = zoom.scale;
      ctx.drawImage(
        backImage,
        ctx.canvas.width / pixelRatio * (1 - scale) / 2,
        ctx.canvas.height / pixelRatio * (1 - scale) / 2,
        Math.ceil(ctx.canvas.width / pixelRatio * scale),
        Math.ceil(ctx.canvas.height / pixelRatio * scale)
      );
      const zone = this.getTransparentZone(ctx);
      if (zone) {
        zones.push([scale, zone]);
      }
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    return zones;
  })

  private frontMedia = resource({
    params: () => this.frontMediaFile()?.upload.image.url,
    loader: ({params: url}): Promise<HTMLImageElement | void> => {
      if (!url) {
        return Promise.resolve();
      }
      return new Promise<HTMLImageElement>(resolve => {
        const image = new Image();
        image.onload = () => {
          resolve(image);
        };
        image.crossOrigin = 'anonymous';
        image.src = url;
      });
    }
  });

  private width = signal(500);
  private pixelRatio = signal(window.devicePixelRatio);

  private currentZoom = 0;
  private chrono = 0;
  private forceRender = true;

  constructor() {
    // Pixel ratio
    const updatePixelRatio = () => {
      const pixelRatio = window.devicePixelRatio;
      this.pixelRatio.set(pixelRatio);
      matchMedia(`(resolution: ${pixelRatio}dppx)`)
        .addEventListener('change', updatePixelRatio, {once: true})
    }
    updatePixelRatio();

    // Resizer
    const once = effect(() => {
      const container = this.container();
      if (!container?.nativeElement) {
        return;
      }
      const observer = new ResizeObserver(entries => {
        entries.forEach(entry => {
          this.width.set(entry.contentRect.width);
        });
      });
      observer.observe(container.nativeElement);
      once.destroy()
    });

    effect(() => {
      this.backImage.value();
      this.frontMedia.value();
      this.width()
      this.zoom();
      this.pixelRatio();
      this.forceRender = true;
    });

    requestAnimationFrame((timestamp) => this.render(timestamp));
  }

  private render(timestamp: number): void {
    if (!this.chrono) {
      this.chrono = timestamp;
    }
    const elapsed = timestamp - this.chrono;
    this.chrono = timestamp;
    const ctx = this.context();
    const pixelRatio = this.pixelRatio()
    const backImage = this.backImage.value();
    const frontImage = this.frontMedia.value();
    const targetZoom = this.zoom();

    if (!ctx || !backImage || !pixelRatio) {
      requestAnimationFrame((t) => this.render(t));
      return;
    }

    const delta = targetZoom - this.currentZoom;
    const sign = Math.sign(delta);
    const speed = 0.002;
    const step = sign < 0 ?
      Math.min(delta, -1) * speed * elapsed :
      Math.max(delta, 1) * speed * elapsed;
    this.currentZoom = sign < 0 ?
      Math.max(targetZoom, this.currentZoom + step) :
      Math.min(targetZoom, this.currentZoom + step);

    ctx.save();

    let currentZone: Zone | null = null;
    // Draw front image
    if (delta || this.forceRender) {
      const transparentZones = this.transparentZones();
      // 2. Calculate the zone to render
      for (let i = 1; i < transparentZones.length; i++) {
        const [prevZoom, prevZone] = transparentZones[i];
        const [nextZoom, nextZone] = transparentZones[i - 1];

        if (this.currentZoom >= prevZoom && this.currentZoom <= nextZoom) {
          const a = (this.currentZoom - prevZoom) / (nextZoom - prevZoom);
          currentZone = {
            x: prevZone.x + (nextZone.x - prevZone.x) * a,
            y: prevZone.y + (nextZone.y - prevZone.y) * a,
            width: prevZone.width + (nextZone.width - prevZone.width) * a,
            height: prevZone.height + (nextZone.height - prevZone.height) * a,
          }
          // 3. Clear zone first to avoid PNG ghost accumulation, then draw
          ctx.clearRect(
            currentZone.x / pixelRatio,
            currentZone.y / pixelRatio,
            currentZone.width / pixelRatio,
            currentZone.height / pixelRatio
          );
          if (frontImage) {
            ctx.drawImage(
              frontImage,
              currentZone.x / pixelRatio,
              currentZone.y / pixelRatio,
              currentZone.width / pixelRatio,
              currentZone.height / pixelRatio
            );
          }
          break;
        }
      }

      ctx.drawImage(
        backImage,
        ctx.canvas.width / pixelRatio * (1 - this.currentZoom) / 2,
        ctx.canvas.height / pixelRatio * (1 - this.currentZoom) / 2,
        ctx.canvas.width / pixelRatio * this.currentZoom,
        ctx.canvas.height / pixelRatio * this.currentZoom
      );
    }

    if (!delta) {
      this.forceRender = false;
    }

    ctx.restore();
    requestAnimationFrame((t) => this.render(t));
  }

  /**
   * Detects transparent zones and calculates their bounding boxes.
   * @returns Array of transparent zones with x, y, width, and height.
   */
  private getTransparentZone(context: CanvasRenderingContext2D): Zone | null {
    const {width, height} = context.canvas;
    const imageData = context.getImageData(0, 0, width, height);
    const data = imageData.data;
    const visited: boolean[][] = Array.from({length: height}, () => Array(width).fill(false));

    const floodFill = (startX: number, startY: number): Zone => {
      const queue = [{x: startX, y: startY}];
      let minX = startX, minY = startY, maxX = startX, maxY = startY;

      while (queue.length > 0) {
        const {x, y} = queue.pop()!;
        if (x < 0 || x >= width || y < 0 || y >= height || visited[y][x]) continue;

        const pixelIndex = (y * width + x) * 4;
        if (data[pixelIndex + 3] !== 255) {
          visited[y][x] = true;

          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);

          queue.push({x: x + 1, y});
          queue.push({x: x - 1, y});
          queue.push({x, y: y + 1});
          queue.push({x, y: y - 1});
        }
      }
      return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
      };
    };
    for (let y = 5; y < height - 5; y++) {
      for (let x = 5; x < width - 5; x++) {
        if (visited[y][x]) continue;
        const pixelIndex = (y * width + x) * 4;
        if (data[pixelIndex + 3] !== 255) {
          const zone = floodFill(x, y);
          if (zone.width > 20 && zone.height > 20 && zone.width * zone.height > 1000) {
            return zone;
          }
        }
        visited[y][x] = true;
      }
    }
    return null;
  }


  download() {
    const dataUrl = this.canvas().nativeElement.toDataURL('image/webp', 1);
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `simulation-${new Date().getTime()}.png`;
    link.click();
  }
}